// ─────────────────────────────────────────────────────────────────────────────
// WP-F3 / REQ-136 — Admin › Vistas de Usuario. Sustituye al `AdminPlaceholder`.
//
// 1:1 con la maqueta (`fundamentales-admin.js`, sección `vistas`): 12 tarjetas con su switch, luego
// «URLs de Micrositios por Fundamental» y el botón Guardar.
//
// 🔴 Dos cosas que la maqueta NO dibuja y aquí hacían falta, con su motivo:
//
//  · **El selector de OU.** La maqueta no tiene ninguno, pero el contrato es por OU y sin selector la
//    pantalla solo podría editar el documento global — dejando la mitad del paquete inalcanzable. Se
//    resuelve como en las secciones hermanas (Notificaciones, Puntos), que ya eligen nivel así. La
//    desviación queda escrita en el §4 de la guía de QA.
//
//  · **La lista de micrositios sale del CATÁLOGO**, no de las cuatro filas fijas de la maqueta, y se
//    llavea por Id. Con cuatro filas fijas, añadir un Fundamental lo dejaría sin micrositio posible y
//    retirar uno desalinearía las URLs en silencio.
//
// 🔴 Y lo que esta pantalla NO decide: seis de las doce vistas tienen además otro interruptor vivo
// (kill-switch, anti-spoiler, toggle de campaña, Wrapped, `reportEnabled`, ser manager). Se COMPONEN
// con Y lógico — apagar aquí oculta la vista; encender aquí no la resucita si su otra regla la tapa.
// La pantalla lo dice, porque si no un admin encendería «Ver a tu equipo» para todos y no pasaría
// nada visible.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useOwnUserViews, useSaveUserViews, useRecognitionCatalog, getRecognitionErrorMessage,
  USER_VIEW_KEYS,
} from '@/api/recognition';
import type { UserViewSettingsDto, UserViewKey } from '@/api/recognition';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { useOUs } from '@/hooks/useOUs';
import { useAdminOuFilter } from './useAdminOuFilter';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';
import { StateBlock } from '../components';
import { AdminSection, AdminSaveBar, AdminToggle } from './adminShared';

/**
 * 🔴 Las vistas de la maqueta que NO tienen una superficie propia en la aplicación, y por eso su
 * tarjeta no se pinta (decisión del usuario, 2026-08-10).
 *
 * <b>Se OCULTAN, no se borran.</b> El campo sigue en el contrato, el documento las sigue guardando y
 * `useVistaHabilitada` las sigue resolviendo: si el cliente decide construirles pantalla, basta con
 * quitarlas de esta lista y cablear su punto de render. Borrarlas del contrato obligaría a rehacer
 * la migración el día que las pidan.
 *
 * Por qué cada una:
 *  · `medallero` y `perfilUsuario` — son la pantalla de perfil, que es la IDENTIDAD del programa: no
 *    hay ninguna OU que vaya a apagarla, y no existe como sección separable.
 *  · `recordatorioDiario` — es un AVISO, no una vista. Su interruptor natural es la sección
 *    Notificaciones, donde ya vive la cadencia.
 *  · `feedActividad` (2026-08-10) — pintaba "Mis Nominaciones" + "Todas las nominaciones" en el
 *    landing de Brillamos Juntos. Nacía en `true` (`USER_VIEWS_DEFAULT`) sin que ningún admin lo
 *    hubiera encendido a propósito, y sus dos CTA de vacío llevaban a `/app/nominar` — pantalla
 *    que el cliente ya había pedido sacar del hero el 2026-08-04 por el mismo motivo. A petición
 *    del cliente se borró el componente entero (`BrillamosFeed.tsx`, ya no existe en el árbol),
 *    no solo su render. Lo único que queda vivo aquí es el campo `feedActividad` del CONTRATO
 *    (`UserViewSettingsDto`, backend) — no se retira porque, a diferencia del componente, sí
 *    obligaría a una migración de datos; por eso se oculta como las otras tres.
 *
 * 🔴 Se ocultan en vez de dejarlas visibles porque un interruptor que persiste y NO apaga nada es
 * peor que no tenerlo: el admin lo mueve, guarda, no pasa nada, y acaba desconfiando de los otros
 * nueve — que sí funcionan. Es la lección de `VisibleToEmployees` en `A5` y de los toggles del Paso 5
 * en `B8`, las dos veces con la misma conclusión.
 */
const VISTAS_SIN_SUPERFICIE: ReadonlySet<UserViewKey> = new Set([
  'medallero', 'perfilUsuario', 'recordatorioDiario', 'feedActividad',
]);

const VISTAS_VISIBLES = USER_VIEW_KEYS.filter((k) => !VISTAS_SIN_SUPERFICIE.has(k));

/**
 * Las vistas que además dependen de OTRA regla. Se marcan en la tarjeta para que nadie crea que este
 * interruptor es la única puerta: encenderlo aquí no resucita una vista que su otra regla tapa.
 *
 * `verATuEquipo` (2026-08-20) — vuelve a tener tarjeta e interruptor real: UAT #56 (2026-08-18) la
 * había retirado porque apagaba «Mi equipo» incluso a un líder real, pero el cliente, al validar la
 * remediación con el equipo técnico de Sigma, pidió explícitamente conservar el interruptor. Vuelve
 * a componerse en su punto de render (botón «Mi equipo» en `PerfilView` y el gate de `/app/equipo`
 * en `TeamView`) con `managerStatus.isManager`, igual que antes de UAT #56.
 */
const VISTAS_COMPUESTAS: Partial<Record<UserViewKey, string>> = {
  enviarReconocimiento: 'sending',
  historicoPorAnio: 'campaign',
  resumenMensual: 'wrapped',
  verATuEquipo: 'manager',
  banderaReportar: 'report',
};

export function AdminVistas() {
  const { t } = useTranslation('recognition');
  const ous = useOUs({ activeOnly: false });
  const catalogo = useRecognitionCatalog();

  // `undefined` = documento GLOBAL. Es el nivel por defecto, igual que en Notificaciones.
  const [ouIdElegido, setOuId] = useState<string | undefined>(undefined);
  // Regla 2026-09-23 — el Admin de OU no tiene el nivel Global (su «OU: Todas»): arranca en su OU y,
  // si solo tiene una, el selector queda bloqueado. Se DERIVA para que la primera consulta ya pida
  // el documento de su OU y no el global.
  const ouFilter = useAdminOuFilter();
  const ouId = ouFilter.ouOptions && !ouFilter.ouOptions.some((o) => o.id === ouIdElegido)
    ? (ouFilter.defaultOuId || undefined)
    : ouIdElegido;
  // Mientras la OU del Admin de OU no se resuelve, no se pide nada: `ouId` indefinido sería el global.
  const query = useOwnUserViews(ouId, !ouFilter.isOuRestricted || ouId !== undefined);
  const save = useSaveUserViews();

  const [draft, setDraft] = useState<UserViewSettingsDto | null>(null);
  const [dirty, setDirty] = useState(false);
  // Último estado «limpio» (cargado o guardado): a esto vuelve «Cancelar cambios» sin recargar.
  const [base, setBase] = useState<UserViewSettingsDto | null>(null);
  useUnsavedChangesGuard(dirty);

  // El borrador se siembra UNA vez por nivel: sin la clave, cualquier refetch (p. ej. al volver el
  // foco a la ventana) descartaría en silencio los toggles recién movidos. Misma lección que
  // AdminNotifications documenta en su propio efecto.
  const nivel = ouId ?? '*';
  const [cargado, setCargado] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data || cargado === nivel) return;
    setDraft(query.data);
    setBase(query.data);
    setCargado(nivel);
    setDirty(false);
  }, [query.data, nivel, cargado]);

  const patch = (p: Partial<UserViewSettingsDto>) => {
    setDraft((d) => (d ? { ...d, ...p } : d));
    setDirty(true);
  };

  const doSave = () => {
    if (!draft) return;
    save.mutate({ body: draft, ouId }, { onSuccess: () => { setBase(draft); setDirty(false); } });
  };

  // Hallazgo de QA (16-oct-2026): faltaba descartar lo no guardado. Restaura el borrador, apaga
  // `dirty` (con lo que el selector de nivel vuelve a habilitarse) y limpia el error de un guardado
  // fallido, todo sin recargar la página.
  const doCancel = () => {
    if (!base) return;
    setDraft(base);
    setDirty(false);
    if (save.isError) save.reset();
  };

  return (
    <AdminSection title={t('admin.views.title')} description={t('admin.views.descLong')}>
      {/* El selector de nivel: la maqueta no lo dibuja, pero sin él solo se podría editar el global. */}
      <label className="adm-field">
        <span>{t('admin.views.scope')}</span>
        <select className="field"
          value={ouId ?? ''}
          disabled={dirty || ouFilter.isLocked}
          onChange={(e) => { setOuId(e.target.value || undefined); setCargado(null); setDraft(null); }}
        >
          {ouFilter.ouOptions
            ? ouFilter.ouOptions.map((ou) => <option key={ou.id} value={ou.id}>{ou.name}</option>)
            : (
              <>
                <option value="">{t('admin.views.scopeGlobal')}</option>
                {(ous.data ?? []).map((ou) => <option key={ou.key} value={ou.key}>{ou.label}</option>)}
              </>
            )}
        </select>
      </label>
      {dirty && <p className="adm-note adm-note--info">{t('admin.views.unsavedLock')}</p>}

      <StateBlock
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={!draft}
        emptyText={t('common.empty')}
        onRetry={() => { query.refetch(); }}
      >
        {draft && (
          <>
            <AdminSaveBar onSave={doSave} saving={save.isPending} dirty={dirty} saved={save.isSuccess} onCancel={doCancel} cancelLabel={t('admin.views.cancelChanges')} />
            {save.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(save.error, t('admin.saveError'))}
              </p>
            )}

            {/* BUG (Julio, 2026-08-18) — sin este contenedor, las tarjetas de switches y la lista de
                micrositios de abajo se veían como un solo bloque continuo, sin ninguna separación
                visual ni espacial entre las dos secciones. */}
            <div className="adm-vistas-section">
            <div className="adm-vistas-grid">
              {VISTAS_VISIBLES.map((clave) => {
                const label = t(`admin.views.card.${clave}.label`);
                return (
                  <div key={clave} className="adm-vista-card">
                    <div className="adm-vista-card-head">
                      <h4>{label}</h4>
                      <FloatingTooltip
                        tooltipClassName="adm-vista-tooltip"
                        content={t(`admin.views.card.${clave}.tooltip`)}
                      >
                        {(anchorProps) => (
                          <button
                            type="button"
                            className="adm-vista-info"
                            aria-label={t('admin.views.infoAria', { label })}
                            {...anchorProps}
                          >
                            <i className="fas fa-circle-info" aria-hidden="true" />
                          </button>
                        )}
                      </FloatingTooltip>
                    </div>
                    <p>{t(`admin.views.card.${clave}.desc`)}</p>
                    {VISTAS_COMPUESTAS[clave] && (
                      <p className="adm-vista-compuesta">
                        <i className="fas fa-link" aria-hidden="true" />{' '}
                        {t(`admin.views.compose.${VISTAS_COMPUESTAS[clave]}`)}
                      </p>
                    )}
                    <AdminToggle isOn={draft[clave]} onChange={(v) => patch({ [clave]: v })} />
                  </div>
                );
              })}
            </div>
            </div>

            {/* CPE-160 — URLs de micrositio, una por Fundamental DEL CATÁLOGO. */}
            <div className="adm-vistas-section">
            <h4 className="adm-sub-title">{t('admin.views.micrositesTitle')}</h4>
            <p className="adm-note">{t('admin.views.micrositesDesc')}</p>
            <div className="adm-fund-list">
              {(catalogo.data ?? []).filter((f) => f.isActive).map((f) => (
                <label key={f.id} className="adm-fund-item">
                  <span className="adm-fund-dot" style={{ background: f.color ?? 'var(--cp-purple)' }} aria-hidden />
                  <span className="adm-fund-name">{f.names?.es ?? f.code}</span>
                  <input
                    type="url"
                    className="adm-vista-url"
                    placeholder="https://…"
                    value={draft.micrositeUrls?.[f.id] ?? ''}
                    onChange={(e) => patch({
                      micrositeUrls: { ...(draft.micrositeUrls ?? {}), [f.id]: e.target.value },
                    })}
                  />
                </label>
              ))}
            </div>
            </div>

            <AdminSaveBar onSave={doSave} saving={save.isPending} dirty={dirty} saved={save.isSuccess} onCancel={doCancel} cancelLabel={t('admin.views.cancelChanges')} />
          </>
        )}
      </StateBlock>
    </AdminSection>
  );
}
